import React, {useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useLocation, useNavigate} from 'react-router-dom'
import {useI18n} from './I18nContext'
import {
  AppLocale,
  LOCALE_FLAGS,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  writeCachedLocale,
} from './locales'
import {appPathAfterLocale, withLocalePath} from './localePaths'

export type LanguageSelectorVariant = 'header' | 'auth'

export function LanguageSelector(props: {
  variant?: LanguageSelectorVariant
  /** Icon-only trigger + popover (e.g. narrow header). Ignored for `auth` variant. */
  compact?: boolean
}) {
  const variant = props.variant ?? 'header'
  const compact = variant === 'header' && !!props.compact
  const {locale, t} = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const goToLocale = useCallback(
    (next: AppLocale) => {
      writeCachedLocale(next)
      const tail = appPathAfterLocale(location.pathname)
      navigate(withLocalePath(next, tail) + (location.search || ''))
    },
    [location.pathname, location.search, navigate],
  )

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    goToLocale(e.target.value as AppLocale)
  }

  const openMenu = () => {
    const el = triggerRef.current
    setAnchor(el ? el.getBoundingClientRect() : null)
    setOpen(true)
  }

  const closeMenu = () => {
    setOpen(false)
    setAnchor(null)
  }

  const pickLocale = (next: AppLocale) => {
    goToLocale(next)
    closeMenu()
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const inner = (
    <>
      <i
        className="fa-regular fa-globe"
        aria-hidden
        style={{
          fontSize: '1.1rem',
          lineHeight: 1,
          flexShrink: 0,
          opacity: 0.85,
        }}
      />
      <label
        className="visually-hidden"
        htmlFor={
          variant === 'header'
            ? 'app-language-select'
            : 'app-language-select-auth'
        }
      >
        {t('lang.selectorAria')}
      </label>
      <select
        id={
          variant === 'header'
            ? 'app-language-select'
            : 'app-language-select-auth'
        }
        className="form-select form-select-sm"
        style={{
          minWidth: variant === 'header' ? 148 : 152,
          maxWidth: 200,
          height: 38,
        }}
        value={locale}
        onChange={onChange}
        aria-label={t('lang.selectorAria')}
        title={LOCALE_LABELS[locale]}
      >
        {SUPPORTED_LOCALES.map((loc) => (
          <option key={loc} value={loc}>
            {`${LOCALE_FLAGS[loc]} ${LOCALE_LABELS[loc]}`}
          </option>
        ))}
      </select>
    </>
  )

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    flexShrink: 0,
  }

  if (variant === 'header' && compact) {
    const popover =
      open && anchor && typeof document !== 'undefined'
        ? createPortal(
            <>
              <div
                className="language-selector__overlay"
                role="presentation"
                onMouseDown={(e) => {
                  e.preventDefault()
                  closeMenu()
                }}
              />
              <div
                className="language-selector__popover language-selector__popover--floating"
                role="listbox"
                aria-label={t('lang.selectorAria')}
                style={{
                  position: 'fixed',
                  top: anchor.bottom + 6,
                  left: anchor.left,
                  minWidth: Math.max(220, anchor.width),
                  zIndex: 10060,
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {SUPPORTED_LOCALES.map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    role="option"
                    aria-selected={loc === locale}
                    className={`language-selector__popover-item${loc === locale ? ' is-active' : ''}`}
                    onClick={() => pickLocale(loc)}
                  >
                    <span
                      className="language-selector__popover-flag"
                      aria-hidden
                    >
                      {LOCALE_FLAGS[loc]}
                    </span>
                    <span>{LOCALE_LABELS[loc]}</span>
                  </button>
                ))}
              </div>
            </>,
            document.body,
          )
        : null

    return (
      <li className="language-selector language-selector--compact-header">
        <button
          ref={triggerRef}
          type="button"
          className="language-selector__compact-trigger"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={t('lang.selectorAria')}
          title={LOCALE_LABELS[locale]}
          onClick={() => (open ? closeMenu() : openMenu())}
        >
          <span className="language-selector__compact-flag" aria-hidden>
            {LOCALE_FLAGS[locale]}
          </span>
          <i
            className="fa-regular fa-chevron-down language-selector__compact-chevron"
            aria-hidden
          />
        </button>
        {popover}
      </li>
    )
  }

  if (variant === 'header') {
    return (
      <li className="language-selector" style={rowStyle}>
        {inner}
      </li>
    )
  }

  return (
    <div className="language-selector language-selector--auth" style={rowStyle}>
      {inner}
    </div>
  )
}
